import React, { useEffect, useState } from 'react'
import mergeWith from 'lodash/mergeWith'
import isArray from 'lodash/isArray'
import { Col, ConfigProvider, Switch, message } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMoon, faSun } from '@fortawesome/free-solid-svg-icons'

// Utils
import CoreEnums from '../utils/enums'

// Components
import LeftMenu from './LeftMenu'
import RightMenu from './RightMenu'
import TabNavigation from './TabNavigation'
import Toolbar from './Toolbar'
import App from '../../custom/app'

// Theme
import { darkTheme, lightTheme } from '../utils/antd-theme'
import { deviceDetect } from 'react-device-detect'
import { readEntitiesPwa } from '../../custom/Admin/entities/utils/utils'
import { useDispatch, useSelector } from 'react-redux'
import coreReducer from '../utils/reducer'
import { handleError } from '../../custom/lib/utils'
import './message.css'
import { isPatient } from '../../custom/lib/profile-utils'
import CustomRow from '../../custom/reusable-components/CustomRow'
import authReducer from '../../custom/Auth/utils/reducer'
import Router from '../../custom/Auth/components/router'
import { signOut } from 'firebase/auth'
import { firebaseAuth } from '../..'
// import SetupWizard from '../../custom/Admin/set-up-wizard/set-up-wizard-wrapper'
import CustomButton from '../../custom/reusable-components/CustomButton'

const Core = (customProps) => {
  const coreState = useSelector((state) => state.core)
  // const authState = useSelector((state) => state.auth)
  const dispatch = useDispatch()
  const [themeButtonDisabled, setThemeButtonDisabled] = useState(false)
  const RootContent = customProps.state.rootContent || App
  const [motionUnit, setMotionUnit] = useState(0.1)
  const customizer = (objValue, srcValue) => {
    if (isArray(objValue)) return srcValue
  }
  const props = mergeWith(Object.assign({}, coreState), Object.assign({}, customProps), customizer)

  // Messages
  message.config({
    duration: 2,
    maxCount: 2,
    prefixCls: deviceDetect().isMobile ? 'agilite_message' : ''
  })

  // Theme
  const [themeToken, setThemeToken] = useState(
    JSON.parse(localStorage.getItem(CoreEnums.localStorage.THEME_TOKEN)) || lightTheme
  )
  const [isDarkTheme, setIsDarkTheme] = useState(
    JSON.parse(localStorage.getItem(CoreEnums.localStorage.IS_DARK_THEME)) || false
  )

  const [lastIdx, setLastIdx] = useState(window.history.state?.idx || 0)

  useEffect(() => {
    // Memoize excluded paths to avoid recreating this array on each render
    const excludedPaths = ['login', 'register', 'checkin']

    const setEntity = async () => {
      const url = window.location.href
      let pathArray = window.location.pathname.split('/')
      let entity = null

      try {
        // Filter out empty values
        pathArray = pathArray.filter((p) => p !== '')

        // Fetch entities
        const entities = await readEntitiesPwa({}, 'customUrl _id')

        if (entities && entities.length > 0) {
          // Create a Map for O(1) lookups by customUrl
          const entitiesMap = new Map()

          // Populate the map once - O(n) operation but only done once
          for (const e of entities) {
            if (e.customUrl) {
              entitiesMap.set(e.customUrl, e)
            }
          }

          // First check: Direct path match using Map (O(1) operation)
          if (pathArray.length > 0 && !excludedPaths.includes(pathArray[0])) {
            entity = entitiesMap.get(pathArray[0])
          }

          // If not found by direct path, try the full URL
          if (!entity) {
            // Optimize the URL check by sorting entities by customUrl length (descending)
            // This ensures we match the most specific URL first
            const sortedEntities = [...entities].sort(
              (a, b) => (b.customUrl ? b.customUrl.length : 0) - (a.customUrl ? a.customUrl.length : 0)
            )

            // Find the first matching entity
            entity = sortedEntities.find((e) => e.customUrl && url.includes(e.customUrl))
          }

          if (entity) {
            const targetEntity = await readEntitiesPwa({ _id: entity._id })
            dispatch(coreReducer.actions.setEntity(targetEntity[0]))
            dispatch(coreReducer.actions.setAppTitle(targetEntity[0].name))
          } else {
            dispatch(coreReducer.actions.setNoEntityFound(true))
          }
        }
      } catch (e) {
        message.error(handleError(e, true))
      }
    }

    setEntity()
    // eslint-disable-next-line
  }, [])

  // useEffect(() => {
  //   if (authState?.agiliteUser?.extraData?.role?.type === 'admin') {
  //     // TODO: Check if user completed tour

  //     handleOpenTour()
  //   }

  //   // eslint-disable-next-line
  // }, [authState?.agiliteUser?.extraData?.role?.type])

  useEffect(() => {
    document.body.style.backgroundColor = themeToken.colorBgBase
    // Change the motion unit to 0.1 again
    setMotionUnit(0.1)
    setThemeButtonDisabled(false)
  }, [themeToken])

  useEffect(() => {
    if (motionUnit === 0) {
      if (isDarkTheme) {
        localStorage.setItem(CoreEnums.localStorage.IS_DARK_THEME, false)
        localStorage.setItem(CoreEnums.localStorage.THEME_TOKEN, JSON.stringify(lightTheme))

        setIsDarkTheme(false)
        setThemeToken(lightTheme)
      } else {
        localStorage.setItem(CoreEnums.localStorage.IS_DARK_THEME, true)
        localStorage.setItem(CoreEnums.localStorage.THEME_TOKEN, JSON.stringify(darkTheme))

        setIsDarkTheme(true)
        setThemeToken(darkTheme)
      }
    }
    // eslint-disable-next-line
  }, [motionUnit])

  const handleThemeToggle = (value) => {
    // We need to set the motion unit to 0 so that there is not transition when changing the theme
    // This state change triggers the useEffect above
    setMotionUnit(0)
    setThemeButtonDisabled(true)
  }

  useEffect(() => {
    // Define the function that will be called when the popstate event is triggered
    const onPopState = (event) => {
      const currentIdx = event.state?.idx || 0

      // If current index is greater than last index, it's forward navigation
      if (currentIdx > lastIdx) {
        setLastIdx(currentIdx)
        return
      }

      // Otherwise it's backward navigation
      setLastIdx(currentIdx)
      if (coreState.tabNavigation.lastActiveKey.length > 0) {
        const targetKey = coreState.tabNavigation.lastActiveKey[coreState.tabNavigation.lastActiveKey.length - 1]
        dispatch(coreReducer.actions.menuItemClick({ key: targetKey, popped: true }))
      }
    }

    window.addEventListener('popstate', onPopState)

    return () => {
      window.removeEventListener('popstate', onPopState)
    }
    // eslint-disable-next-line
  }, [coreState.tabNavigation.activeKey])

  const themeToggle = () => {
    return (
      <Switch
        checked={!isDarkTheme}
        style={{ background: themeToken.token.colorBgContainer }}
        unCheckedChildren={
          <>
            <FontAwesomeIcon icon={faMoon} />
          </>
        }
        checkedChildren={
          <>
            <FontAwesomeIcon color='orange' icon={faSun} />
          </>
        }
        onChange={() => {
          handleThemeToggle()
        }}
        disabled={themeButtonDisabled}
      />
    )
  }

  // const tourSteps = [
  //   {
  //     title: 'Welcome',
  //     description: (
  //       <>
  //         Welcome to the Meta-Clinic Portal.
  //         <br />
  //         <br /> This wizard will guide you through the Entity Set Up process to get you started.
  //       </>
  //     ),
  //     onNext: () => {
  //       dispatch(coreReducer.actions.nextTourStep())
  //     },
  //     prevButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     }
  //   },
  //   {
  //     title: 'Clinic Management',
  //     description: (
  //       <>
  //         This is where you configure various settings for your clinic.
  //         <br />
  //         <br /> Click on the <b>Clinic Management</b> category to get started.
  //       </>
  //     ),
  //     target: () => document.getElementById('clinic_management_category'),
  //     nextButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     },
  //     prevButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     }
  //   },
  //   {
  //     title: 'Clinics',
  //     description: (
  //       <>
  //         This is where you can set up and manage the various clinics of your entity. <br />
  //         <br />
  //         Click on the <b>Clinics</b> menu item.
  //       </>
  //     ),
  //     target: () => document.getElementById('clinics_menu_item'),
  //     nextButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     },
  //     prevButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     }
  //   },
  //   // {
  //   //   title: 'Add Clinic',
  //   //   description: (
  //   //     <>
  //   //       Click on the <b>Add Clinic</b> button to add your first Clinic
  //   //     </>
  //   //   ),
  //   //   target: () => document.getElementById('clinic_add_btn'),
  //   //   nextButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   },
  //   //   prevButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   }
  //   // },
  //   // {
  //   //   title: 'Add Clinic: Form',
  //   //   description: (
  //   //     <>
  //   //       Populate your Clinic's information and click the <b>Add Clinic</b> button to continue.
  //   //     </>
  //   //   ),
  //   //   target: () => document.getElementById('clinic_form'),
  //   //   placement: 'left',
  //   //   nextButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   },
  //   //   prevButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   }
  //   // },
  //   {
  //     title: 'Clinic Added',
  //     description: (
  //       <>
  //         You have successfully created your first clinic. Here you can view and edit your clinics. <br />
  //         <br />
  //         Feel free to navigate back here to add more or edit clinics at any time.
  //       </>
  //     ),
  //     target: () => document.getElementById('clinic_list_view'),
  //     onNext: () => {
  //       dispatch(coreReducer.actions.nextTourStep())
  //     },
  //     prevButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     }
  //   },
  //   {
  //     title: 'Services',
  //     description: (
  //       <>
  //         Services are assigned to Medical Professionals to help patients find the right professional for their needs.
  //         <br />
  //         <br /> Click on the <b>Services</b> menu item.
  //       </>
  //     ),
  //     target: () => document.getElementById('services_menu_item'),
  //     nextButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     },
  //     prevButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     }
  //   },
  //   // {
  //   //   title: 'Add Service',
  //   //   description: (
  //   //     <>
  //   //       Click on the <b>Add Service</b> button to add your first Service
  //   //     </>
  //   //   ),
  //   //   target: () => document.getElementById('service_add_btn'),
  //   //   nextButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   },
  //   //   prevButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   }
  //   // },
  //   // {
  //   //   title: 'Add Service: Form',
  //   //   description: (
  //   //     <>
  //   //       Populate your Service's information and click the <b>Add Service</b> button to continue.
  //   //     </>
  //   //   ),
  //   //   target: () => document.getElementById('service_form'),
  //   //   placement: 'left',
  //   //   nextButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   },
  //   //   prevButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   }
  //   // },
  //   {
  //     title: 'Service Added',
  //     description: (
  //       <>
  //         You have successfully created your first service. Here you can view and edit your services. <br />
  //         <br />
  //         Feel free to navigate back here to add more or edit services at any time.
  //       </>
  //     ),
  //     target: () => document.getElementById('services_list_view'),
  //     onNext: () => {
  //       dispatch(coreReducer.actions.nextTourStep())
  //     },
  //     prevButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     }
  //   },
  //   {
  //     title: 'User Management',
  //     description: (
  //       <>
  //         This is where you can manage and invite users to your entity.
  //         <br />
  //         <br /> Click on the <b>User Management</b> category to get started.
  //       </>
  //     ),
  //     target: () => document.getElementById('user_management_category'),
  //     nextButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     },
  //     prevButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     }
  //   },
  //   {
  //     title: 'System Users',
  //     description: (
  //       <>
  //         Receptionists and other Administrators are regarded as system users, this is where you can add these users.
  //         <br />
  //         <br />
  //         Click on the <b>System Users</b> menu item.
  //       </>
  //     ),
  //     target: () => document.getElementById('system_users_menu_item'),
  //     nextButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     },
  //     prevButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     }
  //   },
  //   // {
  //   //   title: 'Add System User',
  //   //   description: (
  //   //     <>
  //   //       Click on the <b>Invite Invite System User</b> button to invite a System User
  //   //     </>
  //   //   ),
  //   //   target: () => document.getElementById('systemUser_add_btn'),
  //   //   nextButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   },
  //   //   prevButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   }
  //   // },
  //   // {
  //   //   title: 'Invite System User',
  //   //   description: (
  //   //     <>
  //   //       Populate the System User's information and click the <b>Invite</b> button to continue.
  //   //     </>
  //   //   ),
  //   //   target: () => document.getElementById('medProf_form'),
  //   //   placement: 'bottom',
  //   //   nextButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   },
  //   //   prevButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   }
  //   // },
  //   {
  //     title: 'System User Added',
  //     description: (
  //       <>
  //         You have successfully invited your first System User. Here you can view and edit your System Users.
  //         <br />
  //         <br />
  //         Feel free to navigate back here to add more or edit System User Profiles at any time.
  //       </>
  //     ),
  //     onNext: () => {
  //       dispatch(coreReducer.actions.nextTourStep())
  //     },
  //     prevButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     }
  //   },
  //   {
  //     title: 'Medical Professionals',
  //     description: (
  //       <>
  //         This is where you can view and manage your Medical Professionals, such as Doctors, Nurses etc...
  //         <br />
  //         <br /> Lets add your first Medical Professional. Click on the <b>Medical Professional</b> menu item.
  //       </>
  //     ),
  //     target: () => document.getElementById('medical_professionals_menu_item'),
  //     nextButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     },
  //     prevButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     }
  //   },
  //   // {
  //   //   title: 'Add Medical Professional',
  //   //   description: (
  //   //     <>
  //   //       Click on the <b>Invite Medical Professional</b> button to invite a Medical Professional
  //   //     </>
  //   //   ),
  //   //   target: () => document.getElementById('medicalProf_add_btn'),
  //   //   nextButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   },
  //   //   prevButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   }
  //   // },
  //   // {
  //   //   title: 'Invite Medical Professional',
  //   //   description: (
  //   //     <>
  //   //       Populate the Medical Professional's information and click the <b>Invite</b> button to continue.
  //   //     </>
  //   //   ),
  //   //   target: () => document.getElementById('medProf_form'),
  //   //   placement: 'left',
  //   //   nextButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   },
  //   //   prevButtonProps: {
  //   //     style: {
  //   //       display: 'none'
  //   //     }
  //   //   }
  //   // },
  //   {
  //     title: 'Medical Professional Added',
  //     description: (
  //       <>
  //         You have successfully invited your first Medical Professional. Here you can view and edit your Medical
  //         Professionals. <br />
  //         <br />
  //         Feel free to navigate back here to add more or edit Medical Professional Profiles at any time.
  //       </>
  //     ),
  //     onNext: () => {
  //       dispatch(coreReducer.actions.nextTourStep())
  //     },
  //     prevButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     }
  //   },
  //   {
  //     title: 'Patients',
  //     description: (
  //       <>
  //         This is where you can view and manage your patients.
  //         <br />
  //       </>
  //     ),
  //     target: () => document.getElementById('patients_menu_item'),
  //     onNext: () => {
  //       dispatch(coreReducer.actions.closeTour())
  //     },
  //     prevButtonProps: {
  //       style: {
  //         display: 'none'
  //       }
  //     }
  //   }
  // ]

  // const handleOpenTour = () => {
  //   dispatch(
  //     coreReducer.actions.setHiddenLeftMenuItems([
  //       CoreEnums.tabKeys.CLINICAL_ANALYTICS,
  //       CoreEnums.tabKeys.APPOINTMENTS_CALENDAR,
  //       CoreEnums.tabKeys.OCCUPATIONAL_HEALTH,
  //       CoreEnums.tabKeys.EXAMINATION_TEMPLATES,
  //       CoreEnums.tabKeys.SETTINGS
  //     ])
  //   )
  //   dispatch(coreReducer.actions.openTour())
  // }

  // const handleTourFinish = () => {
  //   dispatch(coreReducer.actions.closeTour())
  //   dispatch(coreReducer.actions.setHiddenLeftMenuItems([]))
  //   dispatch(coreReducer.actions.resetTourStep())
  // }

  const handleSignOut = async () => {
    try {
      await signOut(firebaseAuth)
      dispatch(coreReducer.actions.resetState())
      dispatch(coreReducer.actions.enableDisableLeftMenu(false))
      dispatch(coreReducer.actions.enableDisableRightMenu(false))
      dispatch(coreReducer.actions.enableDisableTabs(false))
      dispatch(coreReducer.actions.setRootContent(Router))
      dispatch(authReducer.actions.setUser(null))
      dispatch(authReducer.actions.setAgiliteUser(null))
      dispatch(authReducer.actions.setLoggedIn(false))
    } catch (e) {
      handleError(e, true)
    }
  }
  // if (isPatient()) {
  //   if (deviceDetect().isBrowser) {
  //     return (
  //       <CustomRow
  //         style={{
  //           position: 'fixed',
  //           top: 0,
  //           left: 0,
  //           width: '100vw',
  //           height: '100vh',
  //           margin: 0,
  //           padding: '20px',
  //           background: 'white',
  //           zIndex: 99999999,
  //           display: 'flex',
  //           alignItems: 'center',
  //           justifyContent: 'center'
  //         }}
  //       >
  //         <Col span={24} style={{ textAlign: 'center' }}>
  //           <div>
  //             <h1 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Mobile Access Only</h1>
  //             <p style={{ fontSize: '1.2rem', maxWidth: '600px', margin: '0 auto', marginBottom: '2rem' }}>
  //               For the best experience and security, please access this patient portal using your mobile phone.
  //             </p>
  //             <div style={{ display: 'flex', justifyContent: 'center' }}>
  //               <CustomButton
  //                 onClick={handleSignOut}
  //                 text='Sign Out'
  //                 type='primary'
  //                 danger
  //                 style={{ minWidth: '120px' }}
  //               />
  //             </div>
  //           </div>
  //         </Col>
  //       </CustomRow>
  //     )
  //   }
  // }

  return (
    <ConfigProvider
      theme={{
        token: {
          fontSize: 16,
          ...lightTheme(coreState.entity?.theme).token,
          motionUnit
        },
        components: {
          ...lightTheme(coreState.entity?.theme).components,
          message: {
            colorText: '#fff', // Text color
            colorBg: '#333', // Background color
            colorBorder: '#444' // Border color
          }
        }
      }}
    >
      <div>
        {props.state.toolbar.enabled ? (
          <Toolbar
            isDarkTheme={isDarkTheme}
            themeToggle={themeToggle}
            {...props.state.leftMenu}
            {...props.state.rightMenu}
            {...props.state.toolbar}
            {...props.state.theme}
          />
        ) : null}
        {props.state.leftMenu.leftMenuEnabled && !deviceDetect().isBrowser ? (
          <LeftMenu {...props.state.leftMenu} {...props.state.theme} />
        ) : null}

        {props.state.rightMenu.rightMenuEnabled ? (
          <RightMenu {...props.state.rightMenu} {...props.state.theme} />
        ) : null}
        {props.state.tabNavigation.enabled ? (
          <TabNavigation {...props.state.tabNavigation} {...props.state.leftMenu} {...props} {...props.state.theme} />
        ) : (
          <RootContent />
        )}
      </div>
      {/* {coreState.entity && authState?.agiliteUser?.extraData?.inviteStatus ? (
        <Tour
          open={coreState.tourOpen}
          onClose={() => {}}
          steps={tourSteps}
          closable={false}
          current={coreState.tourStep}
          onFinish={() => {
            handleTourFinish()
          }}
        />
      ) : undefined} */}
    </ConfigProvider>
  )
}

export default Core
